import { useState, type ReactNode } from "react"
import { Loader2, MoreHorizontal, Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  addEndPoints,
  addStartPoints,
  scheduleStatusLabel,
  splitPoints,
  type SchedulePoint,
} from "@/lib/word-form"
import type { FormField, FormHeading, FormSchedule, FormScheduleCorrection } from "@/types/word-form"

// ===================
// Status
// ===================

const STATUS_VARIANT: Record<FormSchedule["status"], "secondary" | "warning" | "success"> = {
  not_started: "secondary",
  in_progress: "warning",
  done: "success",
}

export function ScheduleStatusPill({ schedule }: { schedule: FormSchedule }) {
  return (
    <Badge variant={STATUS_VARIANT[schedule.status]} dot className="whitespace-nowrap text-[10px]">
      {scheduleStatusLabel(schedule)}
    </Badge>
  )
}

/** Low confidence: a clause styled like a schedule, or an annexure. */
export function CheckThisPill() {
  return (
    <Badge
      variant="warning"
      dot={false}
      className="whitespace-nowrap text-[10px]"
      title="This might not be a schedule. Check it, and mark it if it isn't."
    >
      Check this
    </Badge>
  )
}

// ===================
// Panel
// ===================

export interface ScheduleListPanelProps {
  schedules: FormSchedule[]
  /**
   * The form's fields and headings, used to pick where a split or an added
   * schedule starts (headings first, fields as the fallback). Without either,
   * Split and Add are unavailable.
   */
  fields?: FormField[]
  headings?: FormHeading[]
  /** Omit for a read-only list. Resolves true when the change was saved. */
  onCorrect?: (correction: FormScheduleCorrection) => Promise<boolean>
  /** Opens the form page on this schedule. */
  onOpenSchedule?: (scheduleId: string) => void
  isBusy?: boolean
  /** The list is still loading: show a spinner, not "No schedules found". */
  isLoading?: boolean
  /** Why the last correction was refused, shown beside the list or in the open dialog. */
  error?: string | null
  /** Called when a dialog opens or closes, so a stale refusal doesn't linger. */
  onClearError?: () => void
  /**
   * R2 seam: the linked return on the submission checklist. Not rendered
   * until R2 lands (`submissionRequirementId` is always null today).
   */
  renderRequirementLink?: (schedule: FormSchedule) => ReactNode
}

type DialogState =
  | { kind: "rename"; schedule: FormSchedule }
  | { kind: "split"; schedule: FormSchedule }
  | { kind: "add" }
  | null

/**
 * The schedules found in one Word file, with the corrections a person needs
 * when detection is wrong: rename, merge with the next, split, add a missed
 * one, or mark one as not a schedule. Opened from the tender file and the form
 * page; Submit mounts it later.
 */
export function ScheduleListPanel({
  schedules,
  fields = [],
  headings = [],
  onCorrect,
  onOpenSchedule,
  isBusy = false,
  isLoading = false,
  error = null,
  onClearError,
  renderRequirementLink,
}: ScheduleListPanelProps) {
  const [dialog, setDialogState] = useState<DialogState>(null)
  const setDialog = (next: DialogState) => {
    onClearError?.()
    setDialogState(next)
  }
  const canEdit = Boolean(onCorrect)
  const hasFields = fields.length > 0 || headings.length > 0

  const run = async (correction: FormScheduleCorrection) => {
    if (!onCorrect) return
    const ok = await onCorrect(correction)
    if (ok) setDialog(null)
  }

  return (
    <div className="space-y-3">
      {error && !dialog && <CorrectionError message={error} />}
      {isLoading && schedules.length === 0 ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading schedules" />
        </div>
      ) : schedules.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No schedules found.</p>
      ) : (
        <ul aria-label="Schedules" className="divide-y divide-border">
          {schedules.map((schedule, index) => {
            const next = schedules[index + 1]
            return (
              <li
                key={schedule.id}
                data-testid="schedule-row"
                className="flex items-center gap-3 py-2"
              >
                <span className="w-8 shrink-0 text-meta tabular-nums">{schedule.number ?? "—"}</span>
                <div className="min-w-0 flex-1">
                  {onOpenSchedule ? (
                    <button
                      type="button"
                      onClick={() => onOpenSchedule(schedule.id)}
                      className="block max-w-full truncate text-left text-sm hover:text-[hsl(var(--link))]"
                    >
                      {schedule.title}
                    </button>
                  ) : (
                    <p className="truncate text-sm">{schedule.title}</p>
                  )}
                  {schedule.pageLabel && <p className="text-meta">{schedule.pageLabel}</p>}
                </div>
                {schedule.confidence === "low" && <CheckThisPill />}
                {renderRequirementLink?.(schedule)}
                <ScheduleStatusPill schedule={schedule} />
                {canEdit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        aria-label={`Fix ${schedule.title}`}
                        disabled={isBusy}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => setDialog({ kind: "rename", schedule })}>
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!next}
                        onSelect={() =>
                          next &&
                          void run({ op: "merge", scheduleId: schedule.id, withScheduleId: next.id })
                        }
                      >
                        Merge with next
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!hasFields}
                        onSelect={() => setDialog({ kind: "split", schedule })}
                      >
                        Split
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => void run({ op: "dismiss", scheduleId: schedule.id })}
                      >
                        Not a schedule
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {canEdit && (
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasFields || isBusy}
          title={hasFields ? undefined : "Open the form to add a schedule"}
          onClick={() => setDialog({ kind: "add" })}
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add a missed schedule
        </Button>
      )}

      {dialog?.kind === "rename" && (
        <RenameDialog
          error={error}
          schedule={dialog.schedule}
          isBusy={isBusy}
          onCancel={() => setDialog(null)}
          onSave={(title, number) =>
            void run({ op: "rename", scheduleId: dialog.schedule.id, title, number })
          }
        />
      )}
      {dialog?.kind === "split" && (
        <SplitDialog
          error={error}
          schedule={dialog.schedule}
          points={splitPoints(dialog.schedule, schedules, headings, fields)}
          isBusy={isBusy}
          onCancel={() => setDialog(null)}
          onSave={(atPath, title) =>
            void run({ op: "split", scheduleId: dialog.schedule.id, atPath, title })
          }
        />
      )}
      {dialog?.kind === "add" && (
        <AddDialog
          error={error}
          startPoints={addStartPoints(schedules, headings, fields)}
          endPoints={addEndPoints(fields)}
          isBusy={isBusy}
          onCancel={() => setDialog(null)}
          onSave={(input) => void run({ op: "add", ...input })}
        />
      )}
    </div>
  )
}

// ===================
// Dialogs
// ===================

function CorrectionError({ message }: { message: string }) {
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
      {message}
    </p>
  )
}

interface DialogShellProps {
  error?: string | null
  title: string
  description: string
  isBusy: boolean
  canSave: boolean
  saveLabel: string
  onCancel: () => void
  onSave: () => void
  children: ReactNode
}

function DialogShell({
  error = null,
  title,
  description,
  isBusy,
  canSave,
  saveLabel,
  onCancel,
  onSave,
  children,
}: DialogShellProps) {
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {children}
          {error && <CorrectionError message={error} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={!canSave || isBusy}>
            {isBusy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TextField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}

function PointPicker({
  label,
  points,
  value,
  onChange,
}: {
  label: string
  points: SchedulePoint[]
  value: string
  onChange: (path: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Choose one" />
        </SelectTrigger>
        <SelectContent>
          {points.map((point) => (
            <SelectItem key={point.path} value={point.path}>
              {point.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function RenameDialog({
  schedule,
  error,
  isBusy,
  onCancel,
  onSave,
}: {
  schedule: FormSchedule
  error: string | null
  isBusy: boolean
  onCancel: () => void
  onSave: (title: string, number: string | null) => void
}) {
  const [title, setTitle] = useState(schedule.title)
  const [number, setNumber] = useState(schedule.number ?? "")
  return (
    <DialogShell
      error={error}
      title="Rename schedule"
      description="Use the name and number the buyer uses."
      isBusy={isBusy}
      canSave={title.trim().length > 0}
      saveLabel="Save"
      onCancel={onCancel}
      onSave={() => onSave(title.trim(), number.trim() || null)}
    >
      <TextField id="schedule-number" label="Number" value={number} onChange={setNumber} />
      <TextField id="schedule-title" label="Title" value={title} onChange={setTitle} />
    </DialogShell>
  )
}

function SplitDialog({
  schedule,
  points,
  error,
  isBusy,
  onCancel,
  onSave,
}: {
  schedule: FormSchedule
  /** Headings inside the schedule (or its fields after the first). */
  points: SchedulePoint[]
  error: string | null
  isBusy: boolean
  onCancel: () => void
  onSave: (atPath: string, title: string) => void
}) {
  const [atPath, setAtPath] = useState(points[0]?.path ?? "")
  const [title, setTitle] = useState(points[0]?.label ?? "")
  const pick = (path: string) => {
    // Most splits start at a heading: offer its text as the new title.
    const previous = points.find((p) => p.path === atPath)?.label ?? ""
    if (!title.trim() || title === previous) setTitle(points.find((p) => p.path === path)?.label ?? "")
    setAtPath(path)
  }
  return (
    <DialogShell
      error={error}
      title={`Split ${schedule.title}`}
      description="Choose where the second schedule starts."
      isBusy={isBusy}
      canSave={atPath.length > 0 && title.trim().length > 0}
      saveLabel="Split"
      onCancel={onCancel}
      onSave={() => onSave(atPath, title.trim())}
    >
      {points.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          There is nowhere inside this schedule to split it.
        </p>
      ) : (
        <>
          <PointPicker
            label="New schedule starts at"
            points={points}
            value={atPath}
            onChange={pick}
          />
          <TextField id="split-title" label="Title of the new schedule" value={title} onChange={setTitle} />
        </>
      )}
    </DialogShell>
  )
}

function AddDialog({
  startPoints,
  endPoints,
  error,
  isBusy,
  onCancel,
  onSave,
}: {
  startPoints: SchedulePoint[]
  endPoints: SchedulePoint[]
  error: string | null
  isBusy: boolean
  onCancel: () => void
  onSave: (input: { title: string; number: string | null; startPath: string; endPath: string }) => void
}) {
  const [title, setTitle] = useState("")
  const [number, setNumber] = useState("")
  const [startPath, setStartPath] = useState(startPoints[0]?.path ?? "")
  const [endPath, setEndPath] = useState(endPoints[0]?.path ?? "")
  const pickStart = (path: string) => {
    if (!title.trim()) setTitle(startPoints.find((p) => p.path === path)?.label ?? "")
    setStartPath(path)
  }
  return (
    <DialogShell
      error={error}
      title="Add a missed schedule"
      description="Pick where it starts and its last field."
      isBusy={isBusy}
      canSave={title.trim().length > 0 && startPath.length > 0 && endPath.length > 0}
      saveLabel="Add"
      onCancel={onCancel}
      onSave={() =>
        onSave({ title: title.trim(), number: number.trim() || null, startPath, endPath })
      }
    >
      <PointPicker label="Starts at" points={startPoints} value={startPath} onChange={pickStart} />
      <PointPicker label="Last field" points={endPoints} value={endPath} onChange={setEndPath} />
      <TextField id="add-number" label="Number" value={number} onChange={setNumber} />
      <TextField id="add-title" label="Title" value={title} onChange={setTitle} />
    </DialogShell>
  )
}
